import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartLineUp,
  Warning,
  CheckCircle,
  ArrowRight,
  TrendUp,
  Target,
  Question,
  CaretDown,
  Eye,
  Lightbulb,
  Notebook,
  Clock,
  ListChecks,
  ArrowsClockwise,
  CurrencyBtc,
  Lightning,
  ChartBar,
  Shuffle,
  Funnel,
  Coins,
  Graph,
  Crosshair,
  TrendDown,
  Scales,
  UserFocus
} from 'phosphor-react';
import { updateSeoMetadata, getArticleAlternates } from '../../lib/seo';

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

const DiarioDeTradesCripto = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'Diário de Trades para Cripto | Como Melhorar Seus Resultados',
      description: 'Descubra como um diário de trades pode ajudar você a melhorar sua performance no trading de criptomoedas.',
      path: '/pt/diario-de-trades-cripto',
      lang: 'pt-BR',
      alternates: getArticleAlternates('/pt/diario-de-trades-cripto'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  const [openFaq, setOpenFaq] = React.useState(null);

  const faqs = [
    {
      question: "Vale a pena usar diário no trading de cripto?",
      answer: "Absolutamente. O mercado cripto é ainda mais volátil e imprevisível que mercados tradicionais. Sem dados, você está operando no escuro. Um diário de trades cripto permite que você identifique quais moedas você opera bem, quais setups funcionam em diferentes condições de mercado, e onde estão seus erros mais custosos. Traders sérios de cripto tratam dados como ferramenta essencial, não opcional."
    },
    {
      question: "Como analisar trades de criptomoedas?",
      answer: "Comece separando por ativo—BTC, ETH, altcoins de alta cap, altcoins especulativas. Depois analise por setup e condição de mercado. Olhe taxa de acerto, risco/retorno médio, e profit factor para cada categoria. Você provavelmente vai descobrir que tem vantagem em alguns ativos e desvantagem em outros. A análise deve ser granular: resultados agregados escondem informações importantes."
    },
    {
      question: "Preciso registrar todos os trades de cripto?",
      answer: "Quanto mais dados, melhor a análise. Mas consistência importa mais que completude. Se você consegue registrar 90% dos trades de forma consistente, isso é mais valioso que registrar 100% de forma irregular. Um diário automático resolve esse problema ao facilitar o registro. O importante é ter dados suficientes para identificar padrões confiáveis."
    },
    {
      question: "Diário funciona para scalping em cripto?",
      answer: "Sim, mas com ressalvas. Scalpers fazem muitas operações, então o registro manual é impraticável. Você precisa de uma ferramenta que facilite o registro rápido ou importe dados automaticamente. A análise também é diferente: você vai olhar mais para consistência de execução e custos operacionais do que para setups individuais. Volume alto de trades significa mais dados—o que é bom para análise estatística."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <nav className="border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-10 md:h-11 w-auto object-contain"
            />
          </Link>
          <Link 
            to="/signup"
            className="px-4 py-2 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors text-sm"
          >
            Começar Grátis
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 pt-16 pb-20">
        
        <header className="mb-12">
          <div className="flex items-center gap-2 text-[#00E676] text-sm font-medium mb-4">
            <CurrencyBtc size={16} weight="fill" />
            <span>Trading de Cripto</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            Melhor Diário de Trades para Criptomoedas
            <span className="block text-white/60 text-xl md:text-2xl mt-2">
              (Guia Completo)
            </span>
          </h1>
          
          <p className="text-lg text-white/70 leading-relaxed">
            O trading de criptomoedas é um jogo diferente. Volatilidade extrema, mercado 24/7, 
            dezenas de moedas com comportamentos distintos. Essa complexidade exige um 
            <strong className="text-white"> controle de trades cripto</strong> mais sofisticado 
            do que mercados tradicionais. Sem dados organizados, você está jogando dinheiro 
            em um cassino—não operando de forma profissional.
          </p>
        </header>

        {/* Introdução */}
        <section className="mb-14 prose prose-invert max-w-none">
          <p className="text-white/70 leading-relaxed text-lg">
            Um <strong className="text-white">diário de trades cripto</strong> não é luxo—é 
            necessidade. O trader que opera BTC, ETH, Solana e memecoins na mesma semana 
            precisa entender onde está ganhando e onde está perdendo. Misturar tudo em uma 
            análise única produz conclusões erradas.
          </p>
          <p className="text-white/70 leading-relaxed text-lg">
            Este guia vai mostrar como um <strong className="text-white">diário de trading 
            criptomoedas</strong> pode transformar seus resultados, organizando dados de forma 
            que você realmente consiga aprender com suas operações.
          </p>
        </section>

        {/* Seção 1: Por Que Traders de Cripto Precisam de um Diário */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">01</span>
            Por Que Traders de Cripto Precisam de um Diário
          </h2>
          
          <div className="bg-gradient-to-br from-[#F7931A]/10 to-transparent border border-[#F7931A]/20 rounded-lg p-8 mb-8">
            <p className="text-white/80 leading-relaxed mb-4">
              O mercado cripto tem características únicas que tornam o <strong className="text-white">diário 
              de trades bitcoin</strong> e criptomoedas ainda mais importante que em outros mercados:
            </p>
          </div>
          
          <div className="space-y-4 mb-8">
            {[
              {
                razao: "Volume Alto de Operações",
                description: "Muitos traders de cripto fazem dezenas de operações por semana. Sem registro, é impossível lembrar o que funcionou e o que não funcionou.",
                icon: Lightning
              },
              {
                razao: "Volatilidade Extrema",
                description: "Movimentos de 10-20% em um dia são comuns em cripto. Essa volatilidade amplifica erros e acertos—você precisa saber qual é qual.",
                icon: ChartLineUp
              },
              {
                razao: "Múltiplos Ativos",
                description: "BTC, ETH, Solana, altcoins, memecoins—cada um com comportamento diferente. Misturar análises produz resultados inúteis.",
                icon: Coins
              },
              {
                razao: "Dificuldade de Acompanhamento",
                description: "Mercado 24/7, operações em diferentes exchanges, múltiplas carteiras. Sem sistema, o controle fica impossível.",
                icon: Clock
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-[#F7931A]/20 rounded-lg p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-[#F7931A]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={20} weight="duotone" className="text-[#F7931A]" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{item.razao}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Seção 2: Problemas Comuns de Traders de Cripto */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-red-400 text-lg font-mono">02</span>
            Problemas Comuns de Traders de Cripto
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Existem padrões que se repetem entre traders de cripto que não usam 
              <strong className="text-white"> diário de trades</strong>. Esses problemas 
              custam dinheiro e impedem a evolução:
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              {
                problema: "Operar Várias Moedas Sem Controle",
                description: "Entra em BTC, ETH, SOL, memecoin do momento—tudo sem saber onde realmente tem vantagem.",
                icon: Shuffle
              },
              {
                problema: "Não Saber o Que Funciona",
                description: "Usa vários setups diferentes sem dados para saber quais são lucrativos e quais são prejuízo disfarçado.",
                icon: Target
              },
              {
                problema: "Misturar Resultados",
                description: "Analisa performance geral sem separar por ativo. Esconde que está perdendo em altcoins e ganhando só em BTC.",
                icon: Funnel
              },
              {
                problema: "Viés de Memória",
                description: "Lembra das operações grandes que deram certo. Esquece as muitas pequenas que deram errado.",
                icon: Eye
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-red-500/20 rounded-lg p-5"
              >
                <item.icon size={20} weight="duotone" className="text-red-400 mb-2" />
                <h4 className="font-semibold mb-2 text-sm">{item.problema}</h4>
                <p className="text-white/60 text-xs leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Esses problemas são evitáveis. Um <strong className="text-white">diário de trades 
              cripto</strong> bem estruturado resolve cada um deles ao fornecer dados claros 
              e organizados.
            </p>
          </div>
        </section>

        {/* Seção 3: O Que Registrar */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">03</span>
            O Que Registrar em um Diário de Trades
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Para que seu <strong className="text-white">diário de trading criptomoedas</strong> seja 
              útil, você precisa registrar os dados certos. Não adianta só anotar lucro e prejuízo:
            </p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg p-6 mb-8">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <ListChecks size={20} weight="duotone" className="text-[#00E676]" />
              Dados Essenciais
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { dado: "Ativo Negociado", desc: "BTC, ETH, SOL, ou qual altcoin" },
                { dado: "Preço de Entrada", desc: "Valor exato onde você comprou/vendeu" },
                { dado: "Preço de Saída", desc: "Valor exato onde fechou a posição" },
                { dado: "Tamanho da Posição", desc: "Quanto capital foi alocado" },
                { dado: "Stop Loss", desc: "Onde estava seu stop" },
                { dado: "Take Profit", desc: "Qual era seu alvo" },
                { dado: "Setup Utilizado", desc: "Qual padrão motivou a entrada" },
                { dado: "Resultado", desc: "Lucro ou prejuízo em $ e %" }
              ].map((item, index) => (
                <div key={index} className="p-3 bg-white/5 rounded-lg">
                  <h4 className="font-medium text-sm mb-1 text-[#00E676]">{item.dado}</h4>
                  <p className="text-white/50 text-xs">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Com esses dados, você consegue calcular métricas importantes como taxa de acerto, 
              risco/retorno médio, e profit factor—por ativo, por setup, ou de forma geral.
            </p>
          </div>
        </section>

        {/* Seção 4: O Problema de Misturar Diferentes Moedas */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-orange-400 text-lg font-mono">04</span>
            O Problema de Misturar Diferentes Moedas
          </h2>
          
          <div className="bg-gradient-to-br from-orange-500/10 to-transparent border border-orange-500/20 rounded-lg p-8 mb-6">
            <p className="text-white/80 leading-relaxed mb-4">
              Este é um dos erros mais comuns e mais custosos: <strong className="text-white">tratar 
              todas as criptomoedas como se fossem iguais</strong>. Elas não são.
            </p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden mb-8">
            <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/10">
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <CurrencyBtc size={20} weight="fill" className="text-[#F7931A]" />
                  <h4 className="font-semibold text-sm">Bitcoin</h4>
                </div>
                <ul className="space-y-1 text-white/60 text-xs">
                  <li>• Alta liquidez</li>
                  <li>• Movimentos mais previsíveis</li>
                  <li>• Menos manipulação</li>
                  <li>• Referência do mercado</li>
                </ul>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Coins size={20} weight="fill" className="text-blue-400" />
                  <h4 className="font-semibold text-sm">Altcoins (Alta Cap)</h4>
                </div>
                <ul className="space-y-1 text-white/60 text-xs">
                  <li>• Liquidez moderada</li>
                  <li>• Correlação com BTC</li>
                  <li>• Volatilidade maior</li>
                  <li>• Características próprias</li>
                </ul>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Lightning size={20} weight="fill" className="text-purple-400" />
                  <h4 className="font-semibold text-sm">Altcoins (Baixa Cap)</h4>
                </div>
                <ul className="space-y-1 text-white/60 text-xs">
                  <li>• Baixa liquidez</li>
                  <li>• Alta manipulação</li>
                  <li>• Volatilidade extrema</li>
                  <li>• Slippage significativo</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none space-y-4">
            <p className="text-white/70 leading-relaxed">
              <strong className="text-white">BTC ≠ Altcoins.</strong> Um setup que funciona 
              em Bitcoin pode falhar completamente em moedas de baixa capitalização. A liquidez 
              influencia diretamente seus resultados—slippage em altcoins pequenas pode destruir 
              trades que seriam lucrativos em BTC.
            </p>
            <p className="text-white/70 leading-relaxed">
              Se você mistura todos os trades em uma análise única, chega a conclusões erradas. 
              Pode parecer que um setup não funciona quando na verdade funciona em BTC mas não 
              em altcoins. Separar por categoria de ativo é fundamental.
            </p>
          </div>
        </section>

        {/* Seção 5: Como Melhorar no Trading de Cripto com Dados */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">05</span>
            Como Melhorar no Trading de Cripto com Dados
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Ter dados é o primeiro passo. Usar esses dados para melhorar é o que realmente 
              faz diferença. Veja como traders de cripto usam seu <strong className="text-white">diário 
              de trades</strong> para evoluir:
            </p>
          </div>
          
          <div className="space-y-4 mb-8">
            {[
              {
                passo: "Identificar Padrões de Comportamento",
                description: "Descubra quando você opera bem (manhã vs tarde, dias calmos vs voláteis) e ajuste seu horário de trading.",
                icon: Eye
              },
              {
                passo: "Separar Performance por Ativo",
                description: "Analise BTC, ETH e altcoins separadamente. Você provavelmente tem vantagem em alguns e desvantagem em outros.",
                icon: Funnel
              },
              {
                passo: "Avaliar Setups Individualmente",
                description: "Cada setup tem uma taxa de acerto e risco/retorno próprios. Elimine os que não funcionam, dobre os que funcionam.",
                icon: Crosshair
              },
              {
                passo: "Monitorar Risco/Retorno Real",
                description: "Seu R:R planejado pode ser 1:3, mas qual é o R:R que você realmente consegue? Os dados mostram a verdade.",
                icon: Scales
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-[#00E676]/20 rounded-lg p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={20} weight="duotone" className="text-[#00E676]" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{item.passo}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Saiba mais sobre análise de trades no nosso guia sobre 
              <Link to="/pt/diario-de-trades-automatico" className="text-[#00E676] hover:underline"> diário de trades automático</Link>.
            </p>
          </div>
        </section>

        {/* Seção 6: O Que um Bom Diário de Cripto Deve Ter */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">06</span>
            O Que um Bom Diário de Cripto Deve Ter
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Nem todo diário serve para cripto. Você precisa de funcionalidades específicas 
              para esse mercado:
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              {
                feature: "Organização por Moeda",
                description: "Filtrar e analisar trades por BTC, ETH, altcoins específicas. Cada ativo tem sua própria análise.",
                icon: Coins
              },
              {
                feature: "Análise de Performance",
                description: "Cálculo automático de taxa de acerto, profit factor, drawdown por categoria de ativo e por setup.",
                icon: Graph
              },
              {
                feature: "Métricas Claras",
                description: "Dashboard visual mostrando onde você está ganhando e onde está perdendo. Sem ambiguidade.",
                icon: ChartBar
              },
              {
                feature: "Histórico Estruturado",
                description: "Acesso a todos os trades passados com filtros avançados para análise de longo prazo.",
                icon: Clock
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-[#00E676]/20 rounded-lg p-5"
              >
                <item.icon size={24} weight="duotone" className="text-[#00E676] mb-3" />
                <h4 className="font-semibold mb-2">{item.feature}</h4>
                <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Seção 7: Evolução do Trader de Cripto */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">07</span>
            Evolução do Trader de Cripto
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Os melhores traders de cripto não operam por intuição—eles operam por dados. 
              A diferença entre amador e profissional está na qualidade da informação que 
              guia as decisões.
            </p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden mb-8">
            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/10">
              <div className="p-6">
                <h4 className="font-semibold mb-4 text-red-400 flex items-center gap-2">
                  <TrendDown size={18} />
                  Trader Amador
                </h4>
                <ul className="space-y-2 text-white/60 text-sm">
                  <li>• Opera por "feeling"</li>
                  <li>• Não sabe sua taxa de acerto real</li>
                  <li>• Mistura todos os ativos na análise</li>
                  <li>• Repete os mesmos erros</li>
                  <li>• Culpa o mercado pelos prejuízos</li>
                </ul>
              </div>
              <div className="p-6">
                <h4 className="font-semibold mb-4 text-[#00E676] flex items-center gap-2">
                  <TrendUp size={18} />
                  Trader Profissional
                </h4>
                <ul className="space-y-2 text-white/60 text-sm">
                  <li>• Opera por dados</li>
                  <li>• Conhece suas métricas exatas</li>
                  <li>• Analisa cada ativo separadamente</li>
                  <li>• Aprende com cada operação</li>
                  <li>• Assume responsabilidade e ajusta</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              O <strong className="text-white">GENI</strong> ajuda traders de cripto a organizar 
              seus trades automaticamente e comparar performance entre diferentes ativos e setups. 
              É a diferença entre operar no escuro e operar com clareza.
            </p>
          </div>
        </section>

        {/* Conclusão */}
        <section className="mb-14">
          <div className="bg-gradient-to-br from-[#00E676]/10 to-transparent border border-[#00E676]/30 rounded-lg p-8">
            <h3 className="text-xl font-bold mb-4">Conclusão</h3>
            <p className="text-white/80 leading-relaxed mb-4">
              Se você quer melhorar seus resultados em cripto, começar a analisar seus trades 
              é essencial. O mercado de criptomoedas é complexo demais para operar sem dados. 
              Volatilidade, múltiplos ativos, comportamentos diferentes—tudo isso exige um 
              <strong className="text-white"> diário de trades cripto</strong> bem estruturado.
            </p>
            <p className="text-white/70 leading-relaxed">
              Os traders que tratam dados como prioridade são os que eventualmente conseguem 
              consistência. Os que ignoram continuam jogando dinheiro sem entender onde estão 
              errando.
            </p>
          </div>
        </section>

        {/* Links Internos */}
        <section className="mb-14">
          <div className="p-4 border-l-2 border-[#00E676] bg-[#00E676]/5 rounded-r-lg">
            <p className="text-white/80 text-sm mb-3">
              <strong>Leia também:</strong>
            </p>
            <div className="flex flex-col gap-2">
              <Link to="/pt/diario-de-trades-automatico" className="text-[#00E676] hover:underline text-sm">
                O Que é um Diário de Trades Automático →
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 flex items-center gap-3">
            <Question size={28} weight="duotone" className="text-purple-400" />
            Perguntas Frequentes
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium pr-4">{faq.question}</span>
                  <CaretDown 
                    size={18} 
                    className={`text-white/40 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} 
                  />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 pt-0">
                    <p className="text-white/60 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section>
          <div className="bg-gradient-to-br from-[#F7931A]/10 via-[#121212] to-[#00E676]/10 border border-white/10 rounded-lg p-8 md:p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#F7931A]/20 flex items-center justify-center">
              <CurrencyBtc size={32} weight="duotone" className="text-[#F7931A]" />
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Domine Seu Trading de Cripto
            </h2>
            
            <p className="text-white/70 max-w-xl mx-auto mb-8 leading-relaxed">
              Pare de operar no escuro. Registre seus trades, analise sua performance por 
              ativo, e tome decisões baseadas em dados reais.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors"
              >
                Começar Grátis
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-white/10 text-white font-semibold rounded-sm hover:bg-white/20 transition-colors"
              >
                Saiba Mais
              </Link>
            </div>
            
            <p className="text-white/40 text-xs mt-6">
              Sem cartão de crédito · Plano gratuito disponível
            </p>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": "Melhor Diário de Trades para Criptomoedas (Guia Completo)",
              "description": "Descubra como um diário de trades pode ajudar você a melhorar sua performance no trading de criptomoedas.",
              "inLanguage": "pt-BR",
              "author": {
                "@type": "Organization",
                "name": "GENI"
              },
              "publisher": {
                "@type": "Organization",
                "name": "GENI",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://genitradeapp.com/geni-logo.png"
                }
              },
              "datePublished": "2026-01-01",
              "dateModified": new Date().toISOString().split('T')[0],
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "https://genitradeapp.com/pt/diario-de-trades-cripto"
              }
            })
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })
          }}
        />
      </article>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-6 opacity-60"
            />
          </Link>
          <div className="flex items-center gap-6 text-sm text-white/40">
            <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacidade</Link>
            <Link to="/terms" className="hover:text-white/60 transition-colors">Termos</Link>
            <span>© 2026 GENI</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DiarioDeTradesCripto;
